import { motion, useReducedMotion } from 'framer-motion';
import { useSite } from '../content';

export function BronzeSection() {
  const prefersReducedMotion = useReducedMotion();
  const { capabilities } = useSite();

  function scrollToSection(id: string) {
    const element = document.getElementById(id);
    if (!element) return;
    element.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    element.focus({ preventScroll: true });
  }

  return (
    <section
      id="capabilities"
      aria-labelledby="capabilities-heading"
      className="relative w-full py-24 md:py-32 bg-bronze bg-grain overflow-hidden"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.8 }}
          className="text-center mb-16"
        >
          <h2 id="capabilities-heading" className="font-serif text-4xl md:text-5xl text-cream mb-4">
            {capabilities.heading}
          </h2>
          <div aria-hidden="true" className="w-12 h-[1px] bg-cream/50 mx-auto" />
        </motion.div>

        <ul className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-10 list-none p-0">
          {capabilities.plaques.map((plaque, index) => (
            <motion.li
              key={plaque.title}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: prefersReducedMotion ? 0 : 0.8,
                delay: prefersReducedMotion ? 0 : index * 0.2,
              }}
              className="bg-gradient-to-br from-cream to-[#E5DFD8] p-8 md:p-10 rounded-sm shadow-[0_20px_40px_rgba(0,0,0,0.3)] flex flex-col h-full transform hover:-translate-y-2 transition-transform duration-500"
            >
              <h3 className="font-serif text-2xl text-slate-dark mb-4">{plaque.title}</h3>
              <p className="font-sans text-slate-mid/80 leading-relaxed flex-grow mb-6">
                {plaque.description}
              </p>
              <button
                type="button"
                onClick={() => scrollToSection(plaque.linkedServiceId)}
                aria-label={`Explore ${plaque.title}`}
                className="self-start group flex items-center gap-2 text-xs font-sans tracking-widest uppercase text-bronze-light hover:text-bronze transition-colors min-h-[44px]"
              >
                Explore
                <span
                  aria-hidden="true"
                  className="block w-6 h-[1px] bg-bronze-light group-hover:w-10 group-hover:bg-bronze transition-all duration-300"
                />
              </button>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
